/**
 * "Quá hạn" và "hôm nay" — **luật nghiệp vụ**, không phải tiện ích của một màn
 * hình.
 *
 * Trước ở `features/my-tasks/overdue.ts`, nhưng board (bộ lọc Thời hạn) và
 * điều phối cũng hỏi đúng câu đó, mà `features/A` không được import
 * `features/B` (frontend-guide §1). Hai định nghĩa "quá hạn" lệch nhau thì hai
 * màn hình cùng một dự án sẽ nói ngược nhau về cùng một task.
 *
 * `priorityTone` ở lại `features/my-tasks`: nó là màu của một danh sách, không
 * phải một luật.
 */
import { type WorkflowCategory, calendarDateIn } from '@apm/contracts';
import { orgTimezone } from './org-timezone';

/**
 * Quá hạn = có hạn, hạn đã qua, và **chưa xong**.
 *
 * Cùng định nghĩa với bộ lọc `overdue` ở backend (`TaskRepository`). Hai chỗ
 * lệch nhau thì danh sách lọc "quá hạn" và dấu đỏ trên từng dòng sẽ mâu thuẫn
 * nhau ngay trên một màn hình.
 *
 * So sánh chuỗi `YYYY-MM-DD` chứ không dựng `Date`: hạn là một ngày thuần, và
 * `new Date('2026-09-01') < new Date()` kéo theo múi giờ của trình duyệt vào
 * một phép so sánh vốn không có múi giờ. Múi giờ chỉ vào ở **một** chỗ: lúc
 * quy "bây giờ" thành "hôm nay" (`todayIso`).
 */
export function isOverdue(
  dueDate: string | null,
  status: WorkflowCategory,
  today: string,
): boolean {
  if (!dueDate || status === 'done') return false;
  return dueDate < today;
}

/**
 * Ngày **thật sự xong** của một việc, theo lịch của tổ chức — `null` khi chưa
 * xong hoặc không biết.
 *
 * `actualEndAt` là một **mốc** (lúc task vào cột hoàn thành), còn hạn là một
 * ngày thuần: phải quy mốc về ngày của tổ chức trước khi so, cùng cách với
 * `detectDrift` ở API (APM-TZ-g). Cắt `slice(0, 10)` là ra ngày UTC, và việc
 * đóng lúc 6h sáng giờ Việt Nam sẽ bị tính là xong từ hôm trước.
 */
export function completedOn(status: WorkflowCategory, actualEndAt: string | null): string | null {
  if (status !== 'done' || !actualEndAt) return null;
  return calendarDateIn(new Date(actualEndAt), orgTimezone());
}

/**
 * Đã xong, nhưng xong **sau** hạn.
 *
 * Tách hẳn khỏi `isOverdue`: "quá hạn" là danh sách để hành động (bộ lọc, dải
 * "đang chậm deadline") và việc đã đóng không còn gì để làm; còn "xong trễ" là
 * dữ kiện lịch sử — người đọc board, danh sách và Gantt cần thấy nó, nhưng nó
 * không được lọt vào chỗ nào đếm việc cần xử lý.
 *
 * Không có `actualEndAt` (dữ liệu trước APM-TASK-j, Replay) thì trả `false`:
 * không biết ngày xong thì không khẳng định được là xong trễ.
 */
export function isCompletedLate(
  dueDate: string | null,
  status: WorkflowCategory,
  actualEndAt: string | null,
): boolean {
  const doneOn = completedOn(status, actualEndAt);
  return dueDate !== null && doneOn !== null && doneOn > dueDate;
}

/**
 * Hôm nay **của tổ chức**, dạng `YYYY-MM-DD` — APM-TZ-i.
 *
 * Trước đây là ngày UTC: từ 00:00 tới 07:00 sáng giờ Việt Nam "hôm nay" vẫn là
 * hôm qua, nên việc hạn hôm qua chỉ đỏ "quá hạn" từ bảy giờ sáng.
 */
export function todayIso(now = new Date()): string {
  return calendarDateIn(now, orgTimezone());
}
